/* Late Night Lips — Gloss Drip v6
 * Shopify port of the Manus WebGL SDF liquid treatment.
 * Homepage only, pointer-events:none, reduced-motion aware, fail-quiet.
 */
(() => {
  "use strict";
  const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;
  const FRAG = `
precision highp float;

uniform vec2  uRes;      // CSS px
uniform float uDpr;
uniform float uTime;
uniform float uScroll;   // smoothed 0..1
uniform float uTint;     // 0 pink -> 1 cocoa
uniform float uGlobal;   // master fade-in
uniform float uFilm;     // film thickness px
uniform float uR;        // main run radius px
uniform float uLen1;     // main run length px
uniform float uLen2;     // secondary run length px
uniform float uX1;       // main run center x
uniform float uX2;       // secondary run center x
uniform vec4  uDropA;    // x, y, r, stretch
uniform vec4  uDropB;
uniform vec4  uBound;    // topY, rightX, rightBottomY, flowY
uniform float uY2;       // y where the branch forks off the main run

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
    f.y
  );
}

float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

/* pow(x, 2.0) is undefined for x < 0 in GLSL ES — gaussians use sq instead */
float sq(float x) { return x * x; }

/* lateral drift of a run's centerline; pinned (0) at the source so the
   column never detaches from the film */
float sway(float y, float ph) {
  float a = min(1.0, y / 170.0);
  return (sin(y * 0.0050 + uTime * 0.30 + ph) * 5.0 +
          sin(y * 0.0019 - uTime * 0.10 + ph * 2.3) * 8.0) * a;
}

/* a run: capsule with varying radius — slim neck at the source, fuller body
   with slow bulges traveling downward, swelling into a pendant head */
float dRun(vec2 p, float xc, float len, float R, float ph) {
  if (len < 1.0) return 1e6;
  float yq = clamp(p.y, 0.0, len);
  float q = yq / len;
  float x0 = xc + sway(yq, ph);
  float taper = 0.38 + 0.36 * q;
  float bulge = 0.15 * (vnoise(vec2(yq * 0.012 - uTime * 0.5, ph * 3.7)) * 2.0 - 1.0)
              + 0.07 * (vnoise(vec2(yq * 0.004 - uTime * 0.17, ph * 9.1)) * 2.0 - 1.0);
  float head = 0.62 * exp(-sq((yq - len) / (R * 1.7)));
  float r = R * (taper + bulge + head);
  return length(p - vec2(x0, yq)) - r;
}

/* a branch that forks off the main run below the header and drifts
   outward-in toward its own lane, with its own pendant head */
float dBranch(vec2 p, float y0, float yEnd, float R, float ph) {
  if (yEnd < y0 + 30.0) return 1e6;
  float yq = clamp(p.y, y0, yEnd);
  float q = (yq - y0) / (yEnd - y0);
  float tx = smoothstep(y0, y0 + 230.0, yq);
  float x0 = mix(uX1 + sway(yq, 0.0), uX2 + sway(yq, ph), tx);
  float taper = 0.34 + 0.40 * q;
  float bulge = 0.15 * (vnoise(vec2(yq * 0.012 - uTime * 0.5, ph * 3.7)) * 2.0 - 1.0);
  float head = 0.62 * exp(-sq((yq - yEnd) / (R * 1.7)));
  float r = R * (taper + bulge + head);
  return length(p - vec2(x0, yq)) - r;
}

/* thin wavy film hugging the top edge, gathering toward the run source */
float dFilm(vec2 p) {
  float fx = p.x / uRes.x;
  float w = sin(fx * 9.7 + uTime * 0.16) * 0.30
          + sin(fx * 21.3 - uTime * 0.11 + 2.0) * 0.14;
  float h = uFilm * (1.0 + 0.42 * w);
  /* a few isolated, soft incipient droops — texture, not a scallop row */
  h += uFilm * 0.72 * pow(0.5 + 0.5 * sin(fx * 4.2 + 1.15), 8.0);
  h += uFilm * 0.50 * pow(0.5 + 0.5 * sin(fx * 6.9 + 4.0), 10.0);
  /* mass gathering where the main run feeds — a meniscus swell, not a fin */
  h += uR * (0.78 + 0.30 * uScroll) * exp(-sq((p.x - uX1) / (uR * 2.1)));
  return p.y - h;
}

float dDrop(vec2 p, vec4 d) {
  if (d.z < 0.5) return 1e6;
  vec2 q = (p - d.xy) * vec2(1.0, 1.0 / (1.0 + d.w));
  return length(q) - d.z;
}

float field(vec2 p) {
  float k = max(14.0, uR * 0.55);
  float d = dFilm(p);
  d = smin(d, dRun(p, uX1, uLen1, uR, 0.0), k);
  d = smin(d, dBranch(p, uY2, uLen2, uR * 0.58, 4.2), k * 0.85);
  /* two petite fixed drools far from the action — "just a little bit" */
  d = smin(d, dRun(p, uRes.x * 0.165, uFilm * 4.6, uR * 0.30, 2.1), k * 0.7);
  d = smin(d, dRun(p, uRes.x * 0.385, uFilm * 2.9, uR * 0.22, 5.3), k * 0.6);
  d = smin(d, dDrop(p, uDropA), k * 0.55);
  d = smin(d, dDrop(p, uDropB), k * 0.55);
  return d;
}

void main() {
  vec2 p = vec2(gl_FragCoord.x, uRes.y * uDpr - gl_FragCoord.y) / uDpr;

  /* conservative early-out: far from the top film and the right column */
  if (p.y > uBound.x && p.x < uBound.y) { gl_FragColor = vec4(0.0); return; }
  if (p.x >= uBound.y && p.y > uBound.z) { gl_FragColor = vec4(0.0); return; }

  float d = field(p);

  /* soft cast shadow (light from upper left) */
  float shadow = 0.0;
  if (d > 0.0 && d < 70.0) {
    float ds = field(p - vec2(9.0, 18.0));
    shadow = (1.0 - smoothstep(-8.0, 30.0, ds)) * 0.34;
  }

  float aa = 1.0 - smoothstep(-1.4, 1.4, d);
  if (aa <= 0.003 && shadow <= 0.003) { gl_FragColor = vec4(0.0); return; }

  vec3 col = vec3(0.0);
  float alpha = shadow;

  if (aa > 0.003) {
    /* surface normal from the SDF gradient, domed by interior depth */
    vec2 e = vec2(2.2, 0.0);
    float gx = (field(p + e.xy) - field(p - e.xy)) / (2.0 * e.x);
    float gy = (field(p + e.yx) - field(p - e.yx)) / (2.0 * e.x);
    float depth = clamp(-d / (uR * 1.15), 0.0, 1.0);
    float tilt = mix(0.55, 3.4, pow(1.0 - depth, 2.0));
    vec3 n = normalize(vec3(gx * tilt, gy * tilt, 1.0));

    /* palette: hot pink -> rosy molten cocoa as you descend */
    vec3 edgeC = mix(vec3(1.00, 0.35, 0.60), vec3(0.94, 0.55, 0.40), uTint);
    vec3 baseC = mix(vec3(0.94, 0.07, 0.42), vec3(0.55, 0.25, 0.17), uTint);
    vec3 deepC = mix(vec3(0.40, 0.02, 0.16), vec3(0.20, 0.08, 0.06), uTint);

    vec3 albedo = mix(edgeC, baseC, smoothstep(0.0, 0.45, depth));
    albedo = mix(albedo, deepC, smoothstep(0.35, 1.0, depth));

    /* viscous flow striations, drifting slowly downward */
    float streak = vnoise(vec2(p.x * 0.11, p.y * 0.006 - uTime * 0.16));
    albedo *= 0.93 + 0.14 * streak;

    /* lighting */
    vec3 L = normalize(vec3(-0.34, -0.52, 0.78)); /* key, upper-left (y down) */
    float diff = clamp(dot(n, L) * 0.5 + 0.5, 0.0, 1.0);
    vec3 Hv = normalize(L + vec3(0.0, 0.0, 1.0));
    float spec = pow(max(dot(n, Hv), 0.0), 120.0);
    float spec2 = pow(max(dot(n, normalize(vec3(0.5, 0.42, 0.65))), 0.0), 16.0);
    float fres = pow(1.0 - max(n.z, 0.0), 2.6);

    col = albedo * (0.60 + 0.55 * diff);
    col += vec3(1.0, 0.975, 0.985) * spec * 1.32;
    col += albedo * spec2 * 0.28;
    col += mix(vec3(1.0, 0.45, 0.72), vec3(1.0, 0.72, 0.45), uTint) * fres * 0.58;

    /* long vertical window-reflection down the main run */
    float x0m = uX1 + sway(clamp(p.y, 0.0, uLen1), 0.0);
    float refl = exp(-sq((p.x - (x0m - uR * 0.38)) / (uR * 0.20)));
    refl *= smoothstep(0.06, 0.35, depth) * step(p.y, uLen1 + uR);
    col += vec3(1.0, 0.95, 0.98) * refl * 0.25;

    /* a second, wandering studio-strip reflection adds moving wet depth */
    float ribbonX = x0m + sin(p.y * 0.017 - uTime * 0.58) * uR * 0.16;
    float ribbon = exp(-sq((p.x - ribbonX) / (uR * 0.105)));
    ribbon *= smoothstep(0.08, 0.42, depth) * (0.72 + 0.28 * sin(p.y * 0.026 - uTime * 0.72));
    col += vec3(1.0, 0.91, 0.96) * ribbon * 0.13;

    /* pearl micro-glitter riding down with the flow */
    vec2 gp = floor((p - vec2(0.0, uBound.w)) / 2.6);
    float h1 = hash(gp);
    float ph = fract(h1 * 7.31 + uTime * (0.10 + 0.16 * h1));
    float tw = pow(0.5 + 0.5 * cos(6.28318 * ph), 10.0);
    float spark = step(0.895, h1) * tw * smoothstep(0.10, 0.35, depth);
    col += vec3(1.0, 0.92, 0.96) * spark * (0.30 + 1.5 * spec + 0.6 * fres) * (1.0 - 0.35 * uTint);

    col = clamp(col, 0.0, 1.0);
    alpha = max(aa * 0.985, shadow);
    col *= aa * 0.985 / max(alpha, 1e-4);
  }

  gl_FragColor = vec4(col * alpha, alpha) * uGlobal;
}
`;
  const easeInOutCubic = (x) => x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    const canvas = document.querySelector("[data-gloss-drip-canvas]");
    if (canvas?.dataset.glossReady === "true") return;
    if (canvas) canvas.dataset.glossReady = "true";
    if (!canvas) return;
    const gl = (canvas.getContext("webgl2", {
      alpha: true,
      antialias: false,
      premultipliedAlpha: true,
    }) ||
      canvas.getContext("webgl", {
        alpha: true,
        antialias: false,
        premultipliedAlpha: true,
      }));
    if (!gl) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const mk = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn("GlossDrip shader:", gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };
    const vs = mk(gl.VERTEX_SHADER, VERT);
    const fs = mk(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn("GlossDrip link:", gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );
    const aPos = gl.getAttribLocation(prog, "aPos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); /* premultiplied */

    const U = (n) => gl.getUniformLocation(prog, n);
    const u = {
      res: U("uRes"),
      dpr: U("uDpr"),
      time: U("uTime"),
      scroll: U("uScroll"),
      tint: U("uTint"),
      global: U("uGlobal"),
      film: U("uFilm"),
      R: U("uR"),
      len1: U("uLen1"),
      len2: U("uLen2"),
      x1: U("uX1"),
      x2: U("uX2"),
      dropA: U("uDropA"),
      dropB: U("uDropB"),
      bound: U("uBound"),
      y2: U("uY2"),
    };

    let W = 0,
      H = 0,
      dpr = 1;
    const resize = () => {
      const vv = window.visualViewport;
      dpr = Math.min(
        window.devicePixelRatio || 1,
        (vv ? vv.width : window.innerWidth) <= 820 ? 1.5 : 1.6
      );
      W = Math.max(1, Math.round(vv ? vv.width : window.innerWidth));
      H = Math.max(1, Math.round(vv ? vv.height : window.innerHeight));
      canvas.width = Math.max(1, Math.floor(W * dpr));
      canvas.height = Math.max(1, Math.floor(H * dpr));
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      canvas.style.left = (vv ? vv.offsetLeft : 0) + "px";
      canvas.style.top = (vv ? vv.offsetTop : 0) + "px";
      canvas.style.right = "auto";
      canvas.style.bottom = "auto";
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", resize);
      window.visualViewport.addEventListener("scroll", resize);
    }

    let scrollTarget = 0;
    let scroll = 0;
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      scrollTarget = max > 0 ? Math.min(1, window.scrollY / max) : 0;
    };
    onScroll();
    scroll = reduce ? scrollTarget : 0;
    window.addEventListener("scroll", onScroll, { passive: true });

    /* Mobile: drip rides until the first FLASH HIT photo, then disappears.
       One landmark. No glass, no High-shine pin, no piecewise crawl. */
    let exitDocY = null;
    const refreshExit = () => {
      if (W > 768) { exitDocY = null; return; }
      const exitEl =
        document.querySelector(".riot-photo--one") ||
        document.querySelector(".campaign-riot__collage") ||
        document.querySelector("#after-dark");
      if (!exitEl) { exitDocY = null; return; }
      exitDocY = exitEl.getBoundingClientRect().top + window.scrollY;
    };
    refreshExit();
    window.addEventListener("resize", refreshExit);
    let lastExitRefresh = performance.now();

    /* droplet state — at most two in flight */
    const drops = [
      { x: 0, y: 0, v: 0, r: 0, stretch: 0, on: false },
      { x: 0, y: 0, v: 0, r: 0, stretch: 0, on: false },
    ];
    let nextRelease = 4 + Math.random() * 3;

    const swayJS = (y, ph, t, len) => {
      const a = Math.min(1, y / 170);
      return (
        (Math.sin(y * 0.005 + t * 0.3 + ph) * 5 +
          Math.sin(y * 0.0019 - t * 0.1 + ph * 2.3) * 8) *
        a *
        Math.min(1, len)
      );
    };

    let raf = 0;
    let running = true;
    let last = performance.now();
    const start = last;
    let global = 0;

    const onVis = () => {
      running = !document.hidden && !reduce;
      last = performance.now();
      if (running && !raf) raf = requestAnimationFrame(frame);
    };
    document.addEventListener("visibilitychange", onVis);

    const frame = () => {
      raf = 0;
      const now = performance.now();
      let dt = (now - last) / 1000;
      last = now;
      if (dt > 0.05) dt = 0.05;
      const t = reduce ? 4.2 : (now - start) / 1000;

      scroll += (scrollTarget - scroll) * Math.min(1, dt * 3.2);
      if (reduce) scroll = scrollTarget;
      global += (1 - global) * Math.min(1, dt * 1.8);
      if (reduce) global = 1;

      const s = Math.min(1, Math.max(0, scroll));
      const sE = easeInOutCubic(s);

      /* Narrow screens get a lighter treatment: slimmer run, higher start,
         slower descent capped well above the copy. */
      const isNarrow = W <= 768;

      const R = (isNarrow
        ? Math.min(34, Math.max(19, W * 0.052))
        : Math.min(54, Math.max(30, W * 0.032))) * (1 + (isNarrow ? 0.18 : 0.28) * sE);
      const film = (isNarrow
        ? Math.min(9, Math.max(5, H * 0.008))
        : Math.min(12, Math.max(7, H * 0.01))) * (1 + 0.18 * s);
      const x1 = W - Math.min(76, Math.max(isNarrow ? 42 : 50, W * 0.045));
      const x2 = W - Math.min(215, Math.max(145, W * 0.13));
      let len1 = isNarrow
        ? H * (0.16 + 0.84 * easeInOutCubic(Math.min(1, s * 1.05)))
        : H * (0.2 + 0.92 * easeInOutCubic(Math.min(1, s * 0.728)));

      /* Vanish as soon as the first FLASH HIT photo enters the viewport. */
      let exitFade = 1;
      if (isNarrow) {
        if (now - lastExitRefresh > 2000) {
          lastExitRefresh = now;
          setTimeout(refreshExit, 0);
        }
        if (exitDocY != null) {
          const exitInView = exitDocY - window.scrollY;
          len1 = Math.min(len1, Math.max(0, exitInView - 16));
          const fadeK = Math.min(1, Math.max(0, (exitInView - H * 0.08) / (H * 0.28)));
          exitFade = fadeK * fadeK * (3 - 2 * fadeK);
        }
        len1 = Math.min(H - 6, Math.max(0, len1));
      }
      /* the branch forks off the main run below the header and stops
         mid-viewport so it never reaches footer text */
      const y2 = Math.min(150, H * 0.17);
      /* fork only where there's lateral room; tablets keep the single run */
      const appear2 =
        W < 1024 ? 0 : Math.min(1, Math.max(0, (s - 0.05) / 0.12));
      const len2 =
        appear2 > 0 ? y2 + (H * 0.52 - y2) * appear2 * (0.35 + 0.65 * sE) : 0;
      const tint = (() => {
        const k = Math.min(1, Math.max(0, (s - 0.3) / 0.62));
        return (
          k * k * (3 - 2 * k) * 0.88
        ); /* keep a pink undertone even at the footer */
      })();

      /* droplet release: only while the head hangs in-frame */
      const headY1 = Math.min(len1, H - 6);
      const canDrop = len1 < H - 28 && exitFade > 0.4 && !reduce;
      nextRelease -= dt * (0.7 + s * 0.9);
      if (canDrop && nextRelease <= 0) {
        const free = drops.find(d => !d.on);
        if (free) {
          const branchReady = len2 > y2 + 260;
          const fromBranch = branchReady && Math.random() < 0.4;
          const src = fromBranch
            ? { x: x2, len: len2, r: R * 0.58, ph: 4.2 }
            : { x: x1, len: len1, r: R, ph: 0 };
          free.on = true;
          free.x = src.x + swayJS(src.len, src.ph, t, src.len);
          free.y = src.len + src.r * 0.7;
          free.v = 26;
          free.r = src.r * (0.3 + Math.random() * 0.14);
          free.stretch = 0;
        }
        nextRelease = (isNarrow ? 7.5 : 5) + Math.random() * 4.5;
      }
      for (const d of drops) {
        if (!d.on) continue;
        d.v += 560 * dt;
        d.y += d.v * dt;
        d.stretch = Math.min(0.42, d.v / 700);
        if (d.y - d.r > H + 60) d.on = false;
      }

      const boundTop = film * 6.5 + 46;
      const boundRight = x2 - R * 2.0 - 46;
      const dropMaxY = Math.max(
        ...drops.map(d => (d.on ? d.y + d.r * 2.5 : 0)),
        headY1 + R * 2.2,
        len2 + R * 1.6
      );
      const boundRightBottom = Math.min(H + 80, dropMaxY + 60);
      const flowY = t * 9.0; /* glitter rides down slowly */

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(u.res, W, H);
      gl.uniform1f(u.dpr, dpr);
      gl.uniform1f(u.time, t);
      gl.uniform1f(u.scroll, s);
      gl.uniform1f(u.tint, tint);
      gl.uniform1f(u.global, global * 0.98 * exitFade);
      gl.uniform1f(u.film, film);
      gl.uniform1f(u.R, R);
      gl.uniform1f(u.len1, len1);
      gl.uniform1f(u.len2, len2);
      gl.uniform1f(u.x1, x1);
      gl.uniform1f(u.x2, x2);
      const dA = drops[0],
        dB = drops[1];
      gl.uniform4f(u.dropA, dA.x, dA.y, dA.on ? dA.r : 0, dA.stretch);
      gl.uniform4f(u.dropB, dB.x, dB.y, dB.on ? dB.r : 0, dB.stretch);
      gl.uniform4f(u.bound, boundTop, boundRight, boundRightBottom, flowY);
      gl.uniform1f(u.y2, y2);

      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (running && !reduce) raf = requestAnimationFrame(frame);
    };

    if (reduce) {
      running = false;
      /* static pose; re-render on scroll/resize only */
      const rerender = () => {
        if (!raf) raf = requestAnimationFrame(frame);
      };
      window.addEventListener("scroll", rerender, { passive: true });
      window.addEventListener("resize", rerender);
      raf = requestAnimationFrame(frame);
      return () => {
        window.removeEventListener("resize", resize);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("scroll", rerender);
        window.removeEventListener("resize", rerender);
        document.removeEventListener("visibilitychange", onVis);
        if (raf) cancelAnimationFrame(raf);
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      };
    }

    raf = requestAnimationFrame(frame);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVis);
      if (raf) cancelAnimationFrame(raf);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };

})();
